"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalConversation = void 0;
const ramda_1 = require("ramda");
const safe_clone_deep_1 = __importDefault(require("safe-clone-deep"));
const models_1 = require("../models");
const conversation_reducer_1 = require("./conversation-reducer");
const empty_conversation_1 = require("./empty-conversation");
const get_last_operation_description_1 = require("./get-last-operation-description");
const getInitialState = (initialValues) => ({
    ...(0, empty_conversation_1.GET_EMPTY_CONVERSATION)(),
    workspaceId: initialValues.workspaceId,
    myParticipantId: initialValues.participantId,
    participants: {
        ...(0, empty_conversation_1.GET_EMPTY_CONVERSATION)().participants,
        [initialValues.participantId]: {
            ...initialValues.participant,
            status: 'connecting',
            connectUTCTimestamp: Date.now(),
        },
    },
    participantDevices: {
        ...(0, empty_conversation_1.GET_EMPTY_CONVERSATION)().participantDevices,
        [initialValues.participantId]: {
            ...initialValues.participantDevice,
        },
    },
    foci: {
        ...(0, empty_conversation_1.GET_EMPTY_CONVERSATION)().foci,
        [initialValues.participantId]: { fieldName: '', pageId: '' },
    },
});
const isReadonlyArray = (arg) => Array.isArray(arg);
class LocalConversation {
    _conversation;
    _messages;
    constructor(initialValues) {
        this._messages = {
            processed: [],
            all: [],
            undoStack: [],
        };
        if (!initialValues) {
            this._conversation = { ...(0, empty_conversation_1.GET_EMPTY_CONVERSATION)(), workspaceId: '', myParticipantId: '' };
        }
        else if ('participant' in initialValues) {
            this._conversation = getInitialState(initialValues);
        }
        else {
            //use volatilestate
            this._conversation = { ...(0, empty_conversation_1.GET_EMPTY_CONVERSATION)(), ...initialValues };
        }
    }
    _storeMessage = (message) => {
        // store message in mutating array. Array is mutated for efficiency
        // cloning the array would be an unneccesary computational cost
        this._messages.all.push(message);
        this._messages.processed.push(message);
        // remove redo stack if a new material message comes in
        if (this._messages.undoStack.length > 0 &&
            !message.isVolatile &&
            !(0, models_1.isViewportSetMessage)(message)) {
            const messageUserId = this._conversation.participants[message.participantId]?.userId;
            const myUserId = this._conversation.participants[this._conversation.myParticipantId]?.userId;
            if (myUserId && messageUserId === myUserId) {
                this._messages.undoStack = [];
            }
        }
    };
    _processMessage = (message) => {
        this._conversation = (0, conversation_reducer_1.conversationReducer)(this._conversation, message);
    };
    dispatch = (message, options) => {
        //TODO remove
        if (message.messageType === 'state-undo')
            return;
        if (message.messageType === 'state-redo')
            return;
        if (isReadonlyArray(message)) {
            for (let i = 0; i < message.length; i++) {
                this.dispatch(message[i], options);
            }
        }
        else if ((0, models_1.isMessageCollection)(message)) {
            for (let i = 0; i < message.messages.length; i++) {
                this.dispatch(message.messages[i], options);
            }
        }
        else if (options?.doNotSetLocal) {
            if (!(0, models_1.isDirectiveMessage)(message)) {
                this._storeMessage(message);
            }
        }
        else if ((0, models_1.isStateUndoMessage)(message)) {
            const volatileState = this.getVolatileState();
            const messages = this._messages;
            const description = (0, get_last_operation_description_1.getLastOperationDescription)(message.participantId, this._conversation, this.getMessages)?.description;
            const indexes = [];
            const undoneMessages = [];
            messages.processed.forEach((m, i) => {
                if (message.messageIds.includes(m.messageId)) {
                    indexes.push(i);
                    undoneMessages.push(m);
                }
            });
            indexes.reverse().forEach((i) => {
                messages.processed.splice(i, 1);
            });
            this._conversation = { ...(0, empty_conversation_1.GET_EMPTY_CONVERSATION)(), ...volatileState };
            this._messages = {
                processed: [],
                all: [],
                undoStack: [],
            };
            //reprocess messages
            this.dispatch(messages.processed);
            //restore all
            this._messages.all = messages.all;
            this._messages.undoStack = messages.undoStack;
            this._messages.all.push(message);
            //push to undo stack
            this._messages.undoStack.push({
                description: description ?? '',
                messages: [...undoneMessages],
            });
            //restore volatile state (except viewports which should be undone / redone  )
            this.setVolatileState({ ...volatileState, viewports: this._conversation.viewports });
        }
        else if ((0, models_1.isStateRedoMessage)(message)) {
            const messages = this._messages;
            const redo = messages.undoStack.pop();
            if (redo) {
                const volatileState = this.getVolatileState();
                this._conversation = { ...(0, empty_conversation_1.GET_EMPTY_CONVERSATION)(), ...volatileState };
                this._messages = {
                    processed: [],
                    all: [],
                    undoStack: [],
                };
                const newMessages = [...messages.processed, ...redo.messages].sort((a, b) => (a.UTCTimestamp ?? 0) - (b.UTCTimestamp ?? 0));
                //reprocess messages
                this.dispatch(newMessages);
                //restore all
                this._messages.all = messages.all;
                this._messages.undoStack = messages.undoStack;
                messages.all.push(message);
                //restore volatile state (except viewports which should be undone / redone  )
                this.setVolatileState({ ...volatileState, viewports: this._conversation.viewports });
            }
        }
        else {
            if (!(0, models_1.isDirectiveMessage)(message)) {
                if (!options?.doNotSetRemote) {
                    this._storeMessage(message);
                }
                this._processMessage(message);
            }
        }
    };
    getState = () => this._conversation;
    setState = (state) => {
        this._conversation = state;
    };
    getMessages = () => this._messages;
    // volatile state is not persisted
    getVolatileState = () => ({
        foci: this._conversation.foci,
        followingUsers: this._conversation.followingUsers,
        pointers: this._conversation.pointers,
        spotlight: this._conversation.spotlight,
        viewports: this._conversation.viewports,
        alertMessage: this._conversation.alertMessage,
        myParticipantId: this._conversation.myParticipantId,
        workspaceId: this._conversation.workspaceId,
        participantDevices: this._conversation.participantDevices,
        sessionRecordingOptions: this._conversation.sessionRecordingOptions,
        commentary: this._conversation.commentary,
        lastMessageTimestamp: this._conversation.lastMessageTimestamp,
    });
    setVolatileState = (volatileState, setClone) => {
        const source = setClone ? (0, safe_clone_deep_1.default)(volatileState) : volatileState;
        this._conversation = {
            ...this._conversation,
            foci: source.foci,
            followingUsers: source.followingUsers,
            participantDevices: (0, ramda_1.mergeDeepRight)(this._conversation.participantDevices, source.participantDevices),
            pointers: { ...this._conversation.pointers, ...source.pointers },
            spotlight: source.spotlight,
            viewports: { ...this._conversation.viewports, ...source.viewports },
            alertMessage: source.alertMessage,
            lastMessageTimestamp: source.lastMessageTimestamp,
        };
        if (this._conversation.myParticipantId !== source.myParticipantId ||
            this._conversation.workspaceId !== source.workspaceId) {
            throw new Error('inconsistent volatile state being applied');
        }
    };
}
exports.LocalConversation = LocalConversation;
